import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Navbar from './components/Navbar';
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from './pages/AuthPages';
import Dashboard from './pages/Dashboard';
import CreateEvent from './pages/CreateEvent';
import CompletePayment from './pages/CompletePayment';
import EventDetail from './pages/EventDetail';
import Scoreboard from './pages/Scoreboard';
import AllEvents from './pages/AllEvents';
import EventOverview from './pages/EventOverview';
import { OrganizersPage, ParticipantsPage, TeamsPage, PricingTiersPage, ThemeSettingsPage, EmailTemplatesPage, AuditLogPage, AllEventsPage, SponsorsPage, InvoicesPage, AdvertBoardsPage, BodiesOfWaterPage } from './pages/AdminPages';
import ProfilePage from './pages/ProfilePage';
import ParticipantTeams from './pages/ParticipantTeams';
import LandingPage from './pages/LandingPage';
import PublicEvents from './pages/PublicEvents';
import PublicEventOverview from './pages/PublicEventOverview';
import CircuitStandings from './pages/CircuitStandings';
import TeamCircuitHistory from './pages/TeamCircuitHistory';
import EventAnalytics from './pages/EventAnalytics';
import SponsorReport from './pages/SponsorReport';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import './styles/global.css';

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <AuthProvider>
        <Routes>
          {/* Public pages — no auth needed */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/public/events" element={<PublicEvents />} />
          <Route path="/public/events/:eventId" element={<PublicEventOverview />} />
          <Route path="/events/:eventId/scoreboard" element={<Scoreboard />} />

          {/* Auth pages — redirect to dashboard if already logged in */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />

          {/* Protected routes — wrapped in Layout with Navbar */}
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="circuit" element={<AdminRoute><CircuitStandings /></AdminRoute>} />
            <Route path="circuit/teams/:teamId" element={<AdminRoute><TeamCircuitHistory /></AdminRoute>} />
            <Route path="events" element={<AllEvents />} />
            <Route path="events/create" element={<CreateEvent />} />
            <Route path="events/:eventId/pay" element={<CompletePayment />} />
            <Route path="events/:eventId/overview" element={<EventOverview />} />
            <Route path="events/:eventId" element={<EventDetail />} />
            <Route path="events/:eventId/analytics" element={<EventAnalytics />} />
            <Route path="events/:eventId/sponsors/:sponsorId/report" element={<SponsorReport />} />
            <Route path="teams" element={<ParticipantTeams />} />
            <Route path="profile" element={<ProfilePage />} />

            {/* Admin-only routes */}
            <Route path="admin/events" element={<AdminRoute><AllEventsPage /></AdminRoute>} />
            <Route path="admin/organizers" element={<AdminRoute><OrganizersPage /></AdminRoute>} />
            <Route path="admin/participants" element={<AdminRoute><ParticipantsPage /></AdminRoute>} />
            <Route path="admin/teams" element={<AdminRoute><TeamsPage /></AdminRoute>} />
            <Route path="admin/sponsors" element={<AdminRoute><SponsorsPage /></AdminRoute>} />
            <Route path="admin/pricing" element={<AdminRoute><PricingTiersPage /></AdminRoute>} />
            <Route path="admin/theme" element={<AdminRoute><ThemeSettingsPage /></AdminRoute>} />
            <Route path="admin/email-templates" element={<AdminRoute><EmailTemplatesPage /></AdminRoute>} />
            <Route path="admin/audit-log" element={<AdminRoute><AuditLogPage /></AdminRoute>} />
            <Route path="admin/invoices" element={<AdminRoute><InvoicesPage /></AdminRoute>} />
            <Route path="admin/advert-boards" element={<AdminRoute><AdvertBoardsPage /></AdminRoute>} />
            <Route path="admin/bodies-of-water" element={<AdminRoute><BodiesOfWaterPage /></AdminRoute>} />
            <Route path="admin/analytics" element={<AdminRoute><AdminAnalyticsPage /></AdminRoute>} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

// ─── Layout wrapper (Navbar + content via Outlet) ─────────────
function Layout() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar />
      <main style={{ flex: 1 }}>
        <Outlet />
      </main>
    </div>
  );
}

// ─── Route guards ────────────────────────────────────────────
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="spinner" />;
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="spinner" />;
  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
}

function AdminRoute({ children }) {
  const { isAdmin } = useAuth();
  return isAdmin ? children : <Navigate to="/dashboard" />;
}

export default App;
